import React, {
  CSSProperties,
  FunctionComponent,
  useMemo,
  useState,
} from 'react';
import { Skeleton } from '@mui/material';
import { API } from '../../../api/apiConstants';
import { Segment } from '../../../core/formBuilder/usePages';
import { parseSegmentHtml } from '../../@material/textArea/textAreaHtmlParser';
import ImageTextOverlayComponent from '../../commons/ImageTextOverlayComponent';
import { calculateSegmentWidths } from './segmentUtils';

interface SegmentedLayoutProps {
  segments: Segment[] | undefined;
  isMobile: boolean;
  height: string | undefined;
}

const SegmentedLayout: FunctionComponent<SegmentedLayoutProps> = ({
  segments,
  isMobile,
  height,
}) => {
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);

  const layoutStyle: CSSProperties = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row', // responsive: vertical on mobile, horizontal on desktop
    justifyContent: 'space-between',
    minHeight: height + 'vh',
    height: '100%',
    position: 'relative',
  };

  const segmentWidths = useMemo(
    () => calculateSegmentWidths(segments ?? []),
    [segments]
  );

  const renderSegmentContent = (segment: Segment, index: number) => {
    const media = !isMobile ? segment.desktopImage : segment.mobileImage;

    switch (segment.type) {
      case 'image':
        if (!media) return null;
        return (
          <div
            style={{
              width: isMobile ? '100%' : `${segmentWidths[index]}%`,
              height: '100%',
            }}
          >
            {media?.identifier && (
              <img
                src={API.V3.STATIC_RESOURCES.STATIC_RESOURCE(media?.identifier)}
                alt={
                  media?.informativeGraphic && media.alt !== '' ? media.alt : ''
                }
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: media?.style?.objectPosition ?? 'center',
                  display: 'block',
                  overflow: 'hidden',
                }}
                hidden={!imageLoaded}
                onLoad={() => setImageLoaded(true)}
                tabIndex={
                  media?.informativeGraphic && media.alt !== '' ? 0 : -1
                }
              />
            )}
            {!imageLoaded && media?.identifier && (
              <Skeleton
                data-testid="image-skeleton"
                variant="rectangular"
                style={{ width: '100%', height: height + 'vh', flex: '1' }}
              />
            )}
            {media?.identifier && (
              <ImageTextOverlayComponent
                textOverlay={segment?.imageTextOverlay}
              />
            )}
          </div>
        );
      case 'text':
        return (
          <div
            style={{
              background: segment.backgroundColor,
              width: isMobile ? '100%' : `${segmentWidths[index]}%`,
              height: '100%',
              overflow: 'hidden',
            }}
            className="tiptap"
          >
            <style>{` .tiptap p { margin: 0; }`}</style>
            {parseSegmentHtml(segment.text ?? '')}
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div className="segmented-layout" style={layoutStyle}>
      {segments?.map((segment, index) => (
        <React.Fragment key={`${segment.type}_${index}`}>
          {renderSegmentContent(segment, index)}
        </React.Fragment>
      ))}
    </div>
  );
};

export default SegmentedLayout;
